import { useState, useEffect } from 'react';
import {
  User,
  Home,
  TrendingUp,
  Brain,
  Sparkles,
  Activity as ActivityIcon,
  Flame,
  Target,
  ChevronRight
} from 'lucide-react';
import ActivitySkeleton from '../components/skeletons/ActivitySkeleton';
import ProBadge from '../components/ProBadge';
import OnboardingBanner from '../components/OnboardingBanner';
import Yoga from './Yoga';
import MentalMaths from './MentalMaths';
import Meditation from './Meditation';
import Subscription from './Subscription';
import { useDataCache } from '../contexts/DataCacheContext';

type ActivePage = 'home' | 'progress' | 'activity' | 'profile';

interface ActivityPageProps {
  onBack: () => void;
  onNavigate: (page: ActivePage) => void;
  onOpenSubscription?: () => void;
  onStartOnboarding?: () => void;
}

type ActivityView = 'list' | 'yoga' | 'mental_maths' | 'meditation';

// Motivational quotes from Bible, Bhagavad Gita, and famous people
const MOTIVATIONAL_QUOTES = [
  // Bible quotes
  { text: "I can do all things through Christ who strengthens me.", source: "Philippians 4:13", category: "Bible" },
  { text: "For God has not given us a spirit of fear, but of power and of love and of a sound mind.", source: "2 Timothy 1:7", category: "Bible" },
  { text: "Trust in the Lord with all your heart and lean not on your own understanding.", source: "Proverbs 3:5", category: "Bible" },
  { text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.", source: "Joshua 1:9", category: "Bible" },
  { text: "The Lord is my strength and my shield; my heart trusts in him, and he helps me.", source: "Psalm 28:7", category: "Bible" },
  { text: "But those who hope in the Lord will renew their strength. They will soar on wings like eagles.", source: "Isaiah 40:31", category: "Bible" },
  { text: "Commit to the Lord whatever you do, and he will establish your plans.", source: "Proverbs 16:3", category: "Bible" },
  { text: "Do not be anxious about anything, but in every situation, by prayer and petition, present your requests to God.", source: "Philippians 4:6", category: "Bible" },
  { text: "The joy of the Lord is your strength.", source: "Nehemiah 8:10", category: "Bible" },
  { text: "And we know that in all things God works for the good of those who love him.", source: "Romans 8:28", category: "Bible" },
  
  // Bhagavad Gita quotes
  { text: "You have the right to work, but never to the fruit of work.", source: "Bhagavad Gita 2.47", category: "Bhagavad Gita" },
  { text: "The mind is restless and difficult to restrain, but it is subdued by practice.", source: "Bhagavad Gita 6.35", category: "Bhagavad Gita" },
  { text: "Set your heart upon your work but never on its reward.", source: "Bhagavad Gita 2.47", category: "Bhagavad Gita" },
  { text: "A person can rise through the efforts of their own mind; they can also degrade themselves.", source: "Bhagavad Gita 6.5", category: "Bhagavad Gita" },
  { text: "There is neither this world, nor the world beyond. Happiness is for one who doubts not.", source: "Bhagavad Gita 4.40", category: "Bhagavad Gita" },
  { text: "The soul is neither born, and nor does it die.", source: "Bhagavad Gita 2.20", category: "Bhagavad Gita" },
  { text: "Change is the law of the universe. You can be a millionaire or a pauper in an instant.", source: "Bhagavad Gita", category: "Bhagavad Gita" },
  { text: "When meditation is mastered, the mind is unwavering like the flame of a lamp in a windless place.", source: "Bhagavad Gita 6.19", category: "Bhagavad Gita" },
  { text: "Man is made by his belief. As he believes, so he is.", source: "Bhagavad Gita 17.3", category: "Bhagavad Gita" },
  { text: "The wise see knowledge and action as one.", source: "Bhagavad Gita 5.4", category: "Bhagavad Gita" },
  
  // Famous people quotes
  { text: "Small daily improvements lead to staggering long-term results.", source: "Robin Sharma", category: "Famous" },
  { text: "The only way to do great work is to love what you do.", source: "Steve Jobs", category: "Famous" },
  { text: "Be the change that you wish to see in the world.", source: "Mahatma Gandhi", category: "Famous" },
  { text: "In the middle of difficulty lies opportunity.", source: "Albert Einstein", category: "Famous" },
  { text: "The future belongs to those who believe in the beauty of their dreams.", source: "Eleanor Roosevelt", category: "Famous" },
  { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", source: "Winston Churchill", category: "Famous" },
  { text: "The only impossible journey is the one you never begin.", source: "Tony Robbins", category: "Famous" },
  { text: "Your body can stand almost anything. It's your mind that you have to convince.", source: "Andrew Murphy", category: "Famous" },
  { text: "Take care of your body. It's the only place you have to live.", source: "Jim Rohn", category: "Famous" },
  { text: "Health is not valued till sickness comes.", source: "Thomas Fuller", category: "Famous" },
  { text: "The greatest wealth is health.", source: "Virgil", category: "Famous" },
  { text: "Happiness is the highest form of health.", source: "Dalai Lama", category: "Famous" },
  { text: "A healthy outside starts from the inside.", source: "Robert Urich", category: "Famous" },
  { text: "The mind and body are not separate. What affects one, affects the other.", source: "Anonymous", category: "Famous" },
  { text: "Strength does not come from physical capacity. It comes from an indomitable will.", source: "Mahatma Gandhi", category: "Famous" },
  { text: "It is health that is real wealth and not pieces of gold and silver.", source: "Mahatma Gandhi", category: "Famous" },
  { text: "The first wealth is health.", source: "Ralph Waldo Emerson", category: "Famous" },
  { text: "When you arise in the morning, think of what a precious privilege it is to be alive.", source: "Marcus Aurelius", category: "Famous" },
  { text: "Yesterday is gone. Tomorrow has not yet come. We have only today. Let us begin.", source: "Mother Teresa", category: "Famous" },
  { text: "What lies behind us and what lies before us are tiny matters compared to what lies within us.", source: "Ralph Waldo Emerson", category: "Famous" },
];

// Get daily quote based on date (changes every day)
const getDailyQuote = () => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  const diff = today.getTime() - startOfYear.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  const quoteIndex = dayOfYear % MOTIVATIONAL_QUOTES.length;
  return MOTIVATIONAL_QUOTES[quoteIndex];
};

export default function Activity({ onBack, onNavigate, onOpenSubscription, onStartOnboarding }: ActivityPageProps) {
  const {
    userProfile,
    mindBodyStreaks,
    weeklyActivityDays,
    loadUserProfile,
    loadRecentActivities,
    loadMindBodyStreaks,
    isLoadingProfile,
  } = useDataCache();

  const [activeView, setActiveView] = useState<ActivityView>('list');
  const [showSubscription, setShowSubscription] = useState(false);

  useEffect(() => {
    loadUserProfile();
    loadRecentActivities();
    loadMindBodyStreaks();
  }, [loadUserProfile, loadRecentActivities, loadMindBodyStreaks]);

  const getCompletionRate = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysPassedThisWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    const activeDays = weeklyActivityDays || 0;
    return daysPassedThisWeek > 0 ? Math.round((activeDays / daysPassedThisWeek) * 100) : 0;
  };

  const getTotalStreak = () => {
    return mindBodyStreaks.yoga + mindBodyStreaks.mental_maths + mindBodyStreaks.meditation;
  };

  const handleActivityBack = () => {
    setActiveView('list');
    loadMindBodyStreaks();
  };

  if (isLoadingProfile || !userProfile) {
    return <ActivitySkeleton />;
  }

  if (showSubscription) {
    return <Subscription onBack={() => setShowSubscription(false)} />;
  }

  if (activeView === 'yoga') {
    return <Yoga onBack={handleActivityBack} />;
  }

  if (activeView === 'mental_maths') {
    return <MentalMaths onBack={handleActivityBack} />;
  }

  if (activeView === 'meditation') {
    return <Meditation onBack={handleActivityBack} />;
  }

  const completionRate = getCompletionRate();
  const dailyQuote = getDailyQuote();

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Bible': return '✝️';
      case 'Bhagavad Gita': return '🕉️';
      default: return '💫';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'Bible': return 'Biblical Wisdom';
      case 'Bhagavad Gita': return 'Gita Wisdom';
      default: return 'Daily Inspiration';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <header className="bg-white px-5 py-4 flex items-center justify-between sticky top-0 z-10 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <ActivityIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Activity</h1>
              <p className="text-xs text-gray-500">Track your daily progress</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ProBadge onClick={() => setShowSubscription(true)} />
          </div>
        </header>

        <div className="p-4 space-y-4">
          {!userProfile.onboarding_completed && onStartOnboarding && (
            <OnboardingBanner onComplete={onStartOnboarding} />
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            {/* Weekly Progress */}
            <div className="bg-white rounded-2xl p-4 border border-gray-100 col-span-2">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="#f3f4f6"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="34"
                      stroke="url(#progressGradient)"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 34}`}
                      strokeDashoffset={`${2 * Math.PI * 34 * (1 - completionRate / 100)}`}
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#06b6d4" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-black text-gray-900">{completionRate}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Weekly Progress</p>
                  <p className="text-xs text-gray-500 mt-0.5">Keep up the momentum!</p>
                </div>
              </div>
            </div>

            {/* Total Streak */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-100 flex flex-col justify-center items-center">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-2">
                <Flame className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-2xl font-black text-gray-900">{getTotalStreak()}</p>
              <p className="text-[10px] text-gray-500 font-medium">Total Streak</p>
            </div>
          </div>

          {/* Mind & Body Activities */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <Target size={16} className="text-cyan-600" />
                Mind & Body Activities
              </h2>
            </div>

            <div className="divide-y divide-gray-100">
              {/* Yoga */}
              <button
                onClick={() => setActiveView('yoga')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors active:bg-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
                    <ActivityIcon size={22} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">Yoga</p>
                    <p className="text-xs text-gray-500">Stretch & strengthen your body</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Flame size={14} className="text-teal-500" />
                      <span className="text-lg font-bold text-gray-900">{mindBodyStreaks.yoga}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">day streak</p>
                  </div>
                  <ChevronRight size={18} className="text-gray-300" />
                </div>
              </button>

              {/* Mental Maths */}
              <button
                onClick={() => setActiveView('mental_maths')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors active:bg-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <Brain size={22} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">Mental Maths</p>
                    <p className="text-xs text-gray-500">Train your brain daily</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Flame size={14} className="text-blue-500" />
                      <span className="text-lg font-bold text-gray-900">{mindBodyStreaks.mental_maths}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">day streak</p>
                  </div>
                  <ChevronRight size={18} className="text-gray-300" />
                </div>
              </button>

              {/* Meditation */}
              <button
                onClick={() => setActiveView('meditation')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors active:bg-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                    <Sparkles size={22} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">Meditation</p>
                    <p className="text-xs text-gray-500">Find your inner calm</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Flame size={14} className="text-violet-500" />
                      <span className="text-lg font-bold text-gray-900">{mindBodyStreaks.meditation}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">day streak</p>
                  </div>
                  <ChevronRight size={18} className="text-gray-300" />
                </div>
              </button>
            </div>
          </div>

          {/* Motivation Card */}
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-5 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{getCategoryIcon(dailyQuote.category)}</span>
                <span className="text-xs font-bold text-cyan-100 uppercase tracking-wide">
                  {getCategoryLabel(dailyQuote.category)}
                </span>
              </div>
              <p className="text-white font-bold text-base leading-snug">
                "{dailyQuote.text}"
              </p>
              <p className="text-cyan-200 text-xs mt-2">— {dailyQuote.source}</p>
            </div>
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute right-8 -bottom-2 w-16 h-16 bg-white/5 rounded-full" />
          </div>
        </div>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
            <button
              onClick={() => onNavigate('home')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <Home size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Home</span>
            </button>
            <button
              onClick={() => onNavigate('progress')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <TrendingUp size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Progress</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-cyan-600 min-w-[60px] relative">
              <div className="absolute -top-1 w-12 h-0.5 bg-cyan-600 rounded-full"></div>
              <ActivityIcon size={22} strokeWidth={2.5} />
              <span className="text-[10px] font-bold">Activity</span>
            </button>
            <button
              onClick={() => onNavigate('profile')}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
            >
              <User size={22} strokeWidth={2} />
              <span className="text-[10px] font-bold">Profile</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}